import PropTypes from 'prop-types';
import { alpha, makeStyles, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { useMemo } from 'react';
import DotsMenu from '../dotsMenu';
import DropdownMenu from '../dropdownMenu';
import RecruitmentSteps from './recruitmentSteps';
import CustomButton from '../customButton';
import ConditionalTooltip from '../conditionalTooltip';
import ActionButton from '../actionButton';
import UserAvatar from '../userAvatar';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { isArrayEmpty } from '../../../utility/helpers';
import { formatDate } from '../../../utility/dateUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    minWidth: 0,
    borderRadius: 8,
    height: 230,
    boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
    backgroundColor: primary.bluish9,
    marginBottom: spacing(4),
    marginRight: spacing(1.5),
    marginLeft: spacing(1.5),
    cursor: 'pointer',
    '&:hover': {
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.22)}`,
    },
  },
  archivedRoot: {
    height: 146,
  },
  detailsWrapper: {
    backgroundColor: primary.bluish8,
    height: 20,
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    padding: spacing(3, 6),
    marginTop: spacing(8),
    borderBottomRightRadius: 8,
    borderBottomLeftRadius: 8,
  },
  archivedDetailsWrapper: {
    marginTop: spacing(6),
  },
  noStepsDetailsWrapper: {
    marginTop: '-52px',
  },
  jobCardHeaderWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth: '100%',
  },
  jobCardTitleWrapper: {
    flexGrow: 1,
    height: 54,
    paddingTop: spacing(6),
    paddingLeft: spacing(6),
    minWidth: 0,
  },
  jobCardNoActions: {
    maxWidth: 'calc(100% - 45px)',
  },
  title: {
    marginBottom: spacing(2),
  },
  actions: {
    display: 'flex',
    flexShrink: 0,
    paddingTop: spacing(3.75),
    paddingRight: spacing(6.75),
    height: 40,
  },
  iconButton: {
    width: 56,
    height: 40,
    padding: spacing(2, 4, 2, 4),
    marginLeft: spacing(2),
  },
  stepsWrapper: {
    justifyContent: 'center',
    display: 'flex',
    marginTop: spacing(6),
    maxWidth: 'inherit',
    padding: spacing(0, 6, 0, 6),
  },
  button: {
    minWidth: 'fit-content',
    fontSize: 16,
    lineHeight: '24px',
    fontFamily: 'ProximaNova-Bold',
    padding: spacing(2, 4, 2, 4),
    marginLeft: spacing(2),
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: primary.bluish8,
    },
  },
  icon: {
    minWidth: 40,
    height: 40,
  },
  tooltip: {
    display: 'flex',
    flexShrink: 0,
  },
  iconWrapper: {
    width: 18,
    height: 18,
  },
  dropdownMenu: {
    height: 40,
    '&:hover': {
      height: 40,
    },
  },
  detailsButtonWrapper: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  detailsButton: {
    fontSize: 16,
    lineHeight: '24px',
    fontFamily: 'ProximaNova-Bold',
    maxHeight: 40,
    padding: spacing(1, 4, 1, 4),
    marginRight: spacing(2),
    marginLeft: spacing(2),
  },
}));

const DATE_FORMAT = 'MMM d, yyyy';

const JobItem = ({
  translations,
  job,
  candidatesActions,
  actions,
  canEdit,
  canDelete,
  isArchiveView,
  onTeamCreate,
  onSteps,
  onDuplicateJob,
  onUnarchiveJob,
  onDeleteJob,
  onJobClick,
  onJobStepClick,
  onGoToJobDetails,
}) => {
  const classes = useStyles();
  const {
    steps,
    number_of_active_candidates,
    number_of_hired_candidates,
    last_activity,
    created_by,
  } = job;

  const hasNoSteps = !steps || isArrayEmpty(steps);
  const totalCandidates = useMemo(() => {
    if (hasNoSteps) {
      return 0;
    }

    return steps.reduce((acc, step) => {
      return acc + (Number(step.number_of_candidates) || 0);
    }, 0);
  }, [steps, hasNoSteps]);

  const activeCandidates = useMemo(
    () =>
      (Number(number_of_active_candidates) || 0) -
      (Number(number_of_hired_candidates) || 0),
    [number_of_active_candidates, number_of_hired_candidates]
  );

  const unarchivedJobActions = () => {
    return (
      <>
        <CustomButton
          className={classNames(classes.detailsButton)}
          type="withTextLightRounded"
          onClick={onGoToJobDetails}
        >
          {translations.viewJobPostingLabel}
        </CustomButton>
        {canEdit && (
          <>
            <DropdownMenu
              menuItems={candidatesActions}
              buttonLabel={translations.dropdownMenuLabel}
              isDisabled={hasNoSteps}
              disabledTooltipText={translations.disabledAddCandidateAction}
            />
            <CustomButton
              className={classes.button}
              onClick={onTeamCreate}
              type="groupPeopleDarkRoundedOutlined"
            >
              {`${translations.hiringTeamLabel} (${job?.hiring_team_count})`}
            </CustomButton>
            <CustomButton
              className={classes.button}
              onClick={onSteps}
              type="stepsDarkRoundedOutlined"
            >
              {translations.recruitmentStepsLabel}
            </CustomButton>
          </>
        )}
        {(canEdit || canDelete) && (
          <DotsMenu
            menuItems={actions}
            customIconButtonClass={classes.iconButton}
            isSquared
          />
        )}
      </>
    );
  };

  const archivedJobActions = () => {
    return (
      <>
        <ConditionalTooltip
          className={classNames(classes.tooltip)}
          message={translations.details}
          addTooltip
        >
          <ActionButton
            className={classes.icon}
            iconClass={classes.iconWrapper}
            type={ACTION_BUTTON_TYPES.PREVIEW_DESCRIPTION}
            onClickHandler={onGoToJobDetails}
          />
        </ConditionalTooltip>
        {canEdit && (
          <>
            <ConditionalTooltip
              className={classNames(classes.tooltip)}
              message={translations.duplicate}
              addTooltip
            >
              <ActionButton
                className={classes.icon}
                iconClass={classes.iconWrapper}
                type={ACTION_BUTTON_TYPES.DUPLICATE}
                onClickHandler={onDuplicateJob}
              />
            </ConditionalTooltip>
            <ConditionalTooltip
              className={classNames(classes.tooltip)}
              message={translations.unarchive}
              addTooltip
            >
              <ActionButton
                className={classes.icon}
                iconClass={classes.iconWrapper}
                type={ACTION_BUTTON_TYPES.UNARCHIVE}
                onClickHandler={onUnarchiveJob}
              />
            </ConditionalTooltip>
          </>
        )}
        {canDelete && (
          <ConditionalTooltip
            className={classNames(classes.tooltip)}
            message={translations.delete}
            addTooltip
          >
            <ActionButton
              className={classes.icon}
              iconClass={classes.iconWrapper}
              type={ACTION_BUTTON_TYPES.DELETE}
              onClickHandler={onDeleteJob}
            />
          </ConditionalTooltip>
        )}
      </>
    );
  };

  const getJobInfo = () => {
    let jobInfo = job.location
      ? `${job.workspace} · ${job.location}`
      : `${job.workspace}`;
    jobInfo = job.department ? `${job.department.name} · ${jobInfo}` : jobInfo;

    return jobInfo;
  };

  return (
    <div
      className={classNames(classes.root, {
        [classes.archivedRoot]: isArchiveView || hasNoSteps,
      })}
    >
      <div className={classes.jobCardHeaderWrapper} onClick={onJobClick}>
        <div className={classes.jobCardTitleWrapper}>
          <TextBoxWithTooltip
            className={classes.title}
            variant="h3"
            text={job.title}
          />
          <TextBoxWithTooltip variant="body2" text={getJobInfo()} />
        </div>
        <div className={classes.actions}>
          {isArchiveView ? archivedJobActions() : unarchivedJobActions()}
        </div>
      </div>
      {!isArchiveView && (
        <div className={classes.stepsWrapper}>
          <RecruitmentSteps steps={steps} onJobClick={onJobStepClick} />
        </div>
      )}
      <div
        className={classNames(classes.detailsWrapper, {
          [classes.archivedDetailsWrapper]: isArchiveView,
          [classes.noStepsDetailsWrapper]: hasNoSteps && !isArchiveView,
        })}
      >
        <Typography variant="body2">
          {`${translations.createdOnLabel}: `}
        </Typography>
        <Typography variant="subtitle2">
          {formatDate(job.created_at, DATE_FORMAT)}
        </Typography>
        <Typography variant="body2">{translations.separator}</Typography>
        <Typography variant="body2">
          {`${translations.totalCandidatesLabel}: `}
        </Typography>
        <Typography variant="subtitle2">{totalCandidates}</Typography>
        <Typography variant="body2">{translations.separator}</Typography>
        <Typography variant="body2">
          {`${translations.activeCandidatesLabel}: `}
        </Typography>
        <Typography variant="subtitle2">{activeCandidates}</Typography>
        <Typography variant="body2">{translations.separator}</Typography>
        <Typography variant="body2">
          {`${translations.lastActivityLabel}: `}
        </Typography>
        <Typography variant="subtitle2">
          {formatDate(last_activity, DATE_FORMAT) || '-'}
        </Typography>
        <Typography variant="body2">{translations.separator}</Typography>
        <Typography variant="body2">
          {`${translations.createdByLabel}: `}
        </Typography>
        <UserAvatar variant="subtitle2" user={created_by} extraSmall caption />
      </div>
    </div>
  );
};

JobItem.propTypes = {
  translations: PropTypes.object.isRequired,
  job: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  isArchiveView: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  candidatesActions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onJobClick: PropTypes.func.isRequired,
  onJobStepClick: PropTypes.func.isRequired,
  onSteps: PropTypes.func.isRequired,
  onDuplicateJob: PropTypes.func.isRequired,
  onUnarchiveJob: PropTypes.func.isRequired,
  onGoToJobDetails: PropTypes.func.isRequired,
  onDeleteJob: PropTypes.func.isRequired,
  onTeamCreate: PropTypes.func.isRequired,
};

export default JobItem;
